import { getDownloadURL, getStorage, listAll, ref } from "firebase/storage";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import Masonry from "react-masonry-css"; // Import Masonry
import { corsproxyRequest, firebaseApp } from "senf-shared";
import { Icon } from "../../../..";
import { cn } from "../../../../util/classNameConstructor";
import Tag from "../../../atoms/tag/Tag";
import { Input } from "../../../core/inputs/Input";
import type { ImageInput } from "../../imageModifier/ImagesUploadModal";
import { Checkmark } from "../../imageModifier/MediaBrowser";

interface ImageItemProps {
  image: {
    id: string;
    url: string;
    alt: string;
    thumb: string; // Added thumb property
    type: string;
    name?: string;
    size?: number;
  };
  isSelected: boolean;
  onSelect: (image: ImageInput) => void;
}

const ImageItemComponent: React.FC<ImageItemProps> = ({
  image,
  isSelected,
  onSelect,
}) => {
  return (
    <button
      type="button"
      id={image.url}
      onClick={() =>
        onSelect({
          url: image.url,
          alt: image.alt,
          name: image.name || "",
          size: image.size || 0,
          type: image.type,
        })
      }
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onSelect({
            url: image.url,
            alt: image.alt,
            name: image.name || "",
            size: image.size || 0,
            type: image.type,
          });
        }
      }}
      className={cn(
        "relative cursor-pointer user-select-none hover:ring-1 ring-offset-2 ring-border-highlight-default rounded-lg mb-5 overflow-hidden",
        isSelected && "ring-1 ring-offset-2 ring-border-highlight-default",
      )}
    >
      <img
        srcSet={`${image.thumb} 300w, ${image.url} 1000w`}
        sizes="(max-width: 700px) 50vw, 33vw"
        src={image.thumb}
        alt={image.alt}
        className={cn(
          "h-auto w-full object-cover rounded-lg block transition-all duration-300 ease-in-out",
        )}
        loading="lazy"
      />
      <Checkmark checked={isSelected} />
    </button>
  );
};

// Memoize the component
const ImageItem = React.memo(ImageItemComponent);

// removed unused Grid component

interface UnsplashImage {
  urls: {
    regular: string;
    small?: string;
    thumb?: string;
  };
  alt_description: string;
  id: string;
}

interface FirebaseImage {
  url: string;
  id: string;
  alt: string;
  name: string;
}

const UnsplashGallery = ({
  maxImages,
  selectedImages,
  setSelectedImages,
  currentImageUrl,
}: {
  maxImages: number;
  selectedImages: ImageInput[];
  setSelectedImages: React.Dispatch<React.SetStateAction<ImageInput[]>>;
  currentImageUrl: string;
}) => {
  const { t } = useTranslation();
  const [images, setImages] = useState<UnsplashImage[]>([]);
  const [firebaseImages, setFirebaseImages] = useState<FirebaseImage[]>([]);
  const [query, setQuery] = useState("");
  const [loadingImage, setLoadingImage] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState<string | null>(null);

  const galleryRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Ref to store the debounce timeout

  const UNSPLASH_ACCESS_KEY = import.meta.env.VITE_UNSPLASH_ACCESS_KEY;

  // Persist and restore search query per currentImageUrl
  const storageKey = useMemo(() => {
    if (!currentImageUrl) return null;
    try {
      const url = new URL(currentImageUrl);
      return `imagesUploadModal:${url.origin}${url.pathname}`;
    } catch {
      const base =
        currentImageUrl.split("?")[0]?.split("#")[0] || currentImageUrl;
      return `imagesUploadModal:${base}`;
    }
  }, [currentImageUrl]);

  const tagList = [
    "mobility",
    "people",
    "cityscene",
    "miscellaneous",
    "ai",
    "illustration",
  ];

  // Function to fetch images from Firebase storage
  const fetchFirebaseImages = useCallback(async () => {
    // Always read from the EU default bucket. stockImages/ are shared library
    // assets, not user data, so data residency rules don't apply. This avoids
    // an empty gallery for US-region users without requiring bucket mirroring.
    const listRef = ref(getStorage(firebaseApp), "stockImages/");

    try {
      const res = await listAll(listRef);
      const urls = await Promise.all(
        res.items.map(async (item) => {
          const url = await getDownloadURL(item);
          return {
            url,
            id: item.name,
            alt: "", // Use empty alt for Firebase images initially
            name: item.name,
          };
        }),
      );
      setFirebaseImages(urls);
    } catch (error) {
      console.error("Error fetching images from Firebase", error);
    }
  }, []);

  const fetchUnsplashImages = useCallback(
    async (searchQuery: string, page = 1) => {
      setLoadingImage(true);
      try {
        const response = await corsproxyRequest(
          `https://api.unsplash.com/search/photos?query=${searchQuery}&client_id=${UNSPLASH_ACCESS_KEY}&page=${page}&per_page=12`,
        );
        setImages((prevImages) =>
          page === 1 ? response.results : [...prevImages, ...response.results],
        );
        setTotalPages(response.total_pages);
      } catch (error) {
        console.error("Error fetching images from Unsplash", error);
      }
      setLoadingImage(false);
      setSearchPerformed(true);
    },
    [],
  );

  // Debounced search handler
  const handleSearchInputChange = (value?: string) => {
    if (value === undefined) return;
    setSelectedImages([]);

    setQuery(value);

    // Save query right away
    if (storageKey) {
      try {
        const saved = JSON.parse(
          window.sessionStorage.getItem(storageKey) || "{}",
        );
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({ ...saved, query: value }),
        );
      } catch {}
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (value.trim() === "") {
        setImages([]);
      } else {
        setCurrentPage(1);
        fetchUnsplashImages(value, 1);
      }
    }, 500);
  };

  // Restore saved query on mount / when currentImageUrl changes
  useEffect(() => {
    if (!storageKey) return;
    try {
      const saved = JSON.parse(
        window.sessionStorage.getItem(storageKey) || "{}",
      );
      const savedQuery = saved?.query as string | undefined;
      if (typeof savedQuery === "string") {
        setQuery(savedQuery);
        if (savedQuery.trim()) {
          setCurrentPage(1);
          fetchUnsplashImages(savedQuery, 1);
        }
      }
    } catch {}
  }, [storageKey, fetchUnsplashImages]);

  // Handle scrolling to load more Unsplash images when bottom of container is reached
  const handleScroll = useCallback(() => {
    if (!loadingImage && galleryRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = galleryRef.current;
      // Trigger load more slightly before reaching the bottom
      if (
        scrollTop + clientHeight >= scrollHeight - 100 &&
        currentPage < totalPages
      ) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    }
  }, [loadingImage, currentPage, totalPages]);

  // Fetch more Unsplash images when current page changes
  useEffect(() => {
    if (currentPage > 1) {
      fetchUnsplashImages(query, currentPage);
    }
  }, [currentPage, query, fetchUnsplashImages]);

  // Fetch Firebase images on component mount
  useEffect(() => {
    if (query.trim() === "") {
      fetchFirebaseImages();
    }
  }, [query, fetchFirebaseImages]);

  // Add scroll event listener when component mounts
  useEffect(() => {
    const gallery = galleryRef.current;
    if (gallery) {
      gallery.addEventListener("scroll", handleScroll);
      return () => {
        gallery.removeEventListener("scroll", handleScroll); // Cleanup
      };
    }
  }, [handleScroll]);

  // Wrap toggleImageSelection in useCallback
  const toggleImageSelection = useCallback(
    (image: ImageInput) => {
      const alreadySelected = selectedImages.some(
        (selectedImage) => selectedImage.url === image.url,
      );

      if (alreadySelected) {
        setSelectedImages((prevSelectedImages) =>
          prevSelectedImages.filter(
            (selectedImage) => selectedImage.url !== image.url,
          ),
        );
      } else if (maxImages && selectedImages.length >= maxImages) {
        setSelectedImages([...selectedImages.slice(0, maxImages - 1), image]);
      } else {
        setSelectedImages((prevSelectedImages) => [
          ...prevSelectedImages,
          image,
        ]);
      }
    },
    [selectedImages, setSelectedImages, maxImages],
  );

  // Create a memoized map for faster selected checks
  const selectedImagesMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const { url } of selectedImages) {
      map[url] = true;
    }
    return map;
  }, [selectedImages]);

  // Filter images based on the selected tag
  const filteredImages = filter
    ? firebaseImages.filter((image) =>
        image.name.toLowerCase().includes(filter.toLowerCase()),
      )
    : firebaseImages;

  // Define breakpoint columns for Masonry
  const breakpointColumnsObj = {
    default: 3,
    1100: 3,
    700: 2,
    500: 1,
  };

  const normalizeUrl = useCallback((url: string) => {
    try {
      const u = new URL(url);
      return `${u.origin}${u.pathname}`;
    } catch {
      return url.split("?")[0]?.split("#")[0] || url;
    }
  }, []);

  useEffect(() => {
    if (!currentImageUrl || selectedImages.length > 0) return;
    const target = normalizeUrl(currentImageUrl);
    if (query.trim() === "") {
      const found = filteredImages.find(
        (file) => normalizeUrl(file.url) === target,
      );
      if (found) {
        setSelectedImages([
          {
            url: found.url,
            alt: found.alt || "",
            name: found.name || "",
            size: 0,
            type: "image",
          },
        ]);
        document
          .getElementById(found.url)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      const foundUnsplash = images.find(
        (file) => normalizeUrl(file.urls.regular) === target,
      );
      if (foundUnsplash) {
        const url = foundUnsplash.urls.regular;
        setSelectedImages([
          {
            url,
            alt: foundUnsplash.alt_description || "",
            name: "",
            size: 0,
            type: "image",
          },
        ]);
        document
          .getElementById(url)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [
    currentImageUrl,
    selectedImages.length,
    query,
    filteredImages,
    images,
    normalizeUrl,
    setSelectedImages,
  ]);

  return (
    <div className="h-full w-full grid grid-rows-[auto_1fr] max-h-full overflow-hidden flex-shrink-0 flex-grow-0">
      <div className="fixed z-50 top-16 right-3 flex items-center gap-2">
        <Input
          value={query}
          leadingIcon="search"
          onChange={(e) => handleSearchInputChange(e.target.value)}
          placeholder={`${t("util:searchThrough", { val: "Unsplash" })}...`}
          size="md"
          className="w-56"
        />
      </div>
      {!query.length ? (
        <div className="px-5 py-3 flex gap-3 border-b border-border-subdued">
          {tagList.map((tag) => (
            <Tag
              key={tag}
              text={t(`topics:${tag}`)}
              onClick={() => setFilter(filter === tag ? null : tag)}
              color={filter === tag ? "blue" : "light-grey"}
            />
          ))}
        </div>
      ) : null}
      <div ref={galleryRef} className="overflow-y-auto min-h-0 p-5">
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="flex w-auto -ml-5"
          columnClassName="pl-5 bg-clip-padding"
        >
          {/* Always render current images, even while loading more */}
          {query.trim() === ""
            ? filteredImages.map((fbImage) => (
                <ImageItem
                  key={fbImage.id}
                  image={{ ...fbImage, thumb: fbImage.url, type: "image" }}
                  isSelected={!!selectedImagesMap[fbImage.url]}
                  onSelect={toggleImageSelection}
                />
              ))
            : images.map((unsplashImage) => {
                const imageForRender = {
                  id: unsplashImage.id,
                  url: unsplashImage.urls.regular,
                  thumb: unsplashImage.urls.thumb || unsplashImage.urls.regular,
                  alt: unsplashImage.alt_description || "",
                  type: "image",
                };
                return (
                  <ImageItem
                    key={imageForRender.id}
                    image={imageForRender}
                    isSelected={!!selectedImagesMap[imageForRender.url]}
                    onSelect={toggleImageSelection}
                  />
                );
              })}
        </Masonry>

        {/* Loading indicator shown below the grid while loading */}
        {loadingImage ? (
          <div className="col-span-full flex justify-center items-center p-5">
            <Icon name="loading" />
          </div>
        ) : null}

        {/* No images found message */}
        {!loadingImage &&
          filteredImages.length === 0 &&
          images.length === 0 &&
          searchPerformed && (
            <div className="col-span-full text-center p-5">No images found</div>
          )}
      </div>
    </div>
  );
};

export default UnsplashGallery;
